/**
 * Client-side Arena constants + view types. Imports only type-only + pure
 * const-array exports from lib/arena/types (zod-only runtime dep — safe in the
 * browser bundle). No DB/server code reaches here.
 */
import type {
  ArenaModality,
  ArenaGenerationStatus,
  ArenaExperimentStatus,
  BlindGenerationView,
  ArenaGenerationView,
} from '@/lib/arena/types';

export type { BlindGenerationView, ArenaGenerationView } from '@/lib/arena/types';

export interface ModalityMeta {
  value: ArenaModality;
  label: string;
  /** Tailwind text/bg accent classes for the badge. */
  accent: string;
  blurb: string;
}

export const MODALITY_META: Record<ArenaModality, ModalityMeta> = {
  image: {
    value: 'image',
    label: 'Image',
    accent: 'text-sky-300 bg-sky-500/10 border-sky-500/25',
    blurb: 'Still-image generation across models/prompts.',
  },
  video: {
    value: 'video',
    label: 'Video',
    accent: 'text-violet-300 bg-violet-500/10 border-violet-500/25',
    blurb: 'Video clip generation — speed + frame quality.',
  },
  avatar: {
    value: 'avatar',
    label: 'Avatar',
    accent: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/25',
    blurb: 'Talking-head avatar clips from a source image + audio.',
  },
  'html-viz': {
    value: 'html-viz',
    label: 'HTML viz',
    accent: 'text-amber-300 bg-amber-500/10 border-amber-500/25',
    blurb: 'Rendered HTML/code visualizations (screenshot + code).',
  },
};

export const STATUS_ACCENT: Record<ArenaExperimentStatus, string> = {
  draft: 'text-muted-foreground bg-muted/60 border-border',
  running: 'text-sky-300 bg-sky-500/10 border-sky-500/25',
  completed: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/25',
  failed: 'text-red-300 bg-red-500/10 border-red-500/25',
  budget_exhausted: 'text-amber-300 bg-amber-500/10 border-amber-500/25',
  canceled: 'text-muted-foreground bg-muted/60 border-border',
};

export const STATUS_LABEL: Record<ArenaExperimentStatus, string> = {
  draft: 'Draft',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  budget_exhausted: 'Budget exhausted',
  canceled: 'Canceled',
};

export const GEN_STATUS_ACCENT: Record<ArenaGenerationStatus, string> = {
  queued: 'text-muted-foreground bg-muted/60 border-border',
  running: 'text-sky-300 bg-sky-500/10 border-sky-500/25',
  succeeded: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/25',
  failed: 'text-red-300 bg-red-500/10 border-red-500/25',
};

/** Curated, extensible defect taxonomy (B2). */
export const DEFECT_TAGS = [
  'bad-anatomy',
  'text-illegible',
  'off-brand',
  'artifacting',
  'wrong-style',
  'too-slow-motion',
  'layout-broken',
  'other',
] as const;
export type DefectTag = (typeof DEFECT_TAGS)[number];

/** 1–5 rubric dimensions per modality (B2). */
export const RUBRIC_DIMENSIONS: Record<ArenaModality, string[]> = {
  image: ['fidelity', 'prompt-adherence', 'aesthetics'],
  video: ['fidelity', 'prompt-adherence', 'aesthetics'],
  avatar: ['fidelity', 'prompt-adherence', 'aesthetics'],
  'html-viz': ['layout', 'legibility', 'aesthetics'],
};

/** Whether a generation status is a terminal, reviewable outcome. */
export function isReviewable(gen: Pick<BlindGenerationView, 'status' | 'artifactUrl' | 'extraArtifacts'>): boolean {
  if (gen.status !== 'succeeded') return false;
  return Boolean(gen.artifactUrl) || Boolean(gen.extraArtifacts);
}

/** Best media URL for a card (screenshot/poster fallback to artifact). */
export function posterUrlFor(gen: Pick<BlindGenerationView, 'artifactUrl' | 'extraArtifacts'>): string | null {
  const extra = gen.extraArtifacts ?? {};
  const screenshot = extra['screenshotUrl'];
  const poster = extra['posterUrl'];
  const thumb = extra['thumbnailUrl'];
  if (typeof screenshot === 'string') return screenshot;
  if (typeof poster === 'string') return poster;
  if (typeof thumb === 'string') return thumb;
  return gen.artifactUrl;
}

/** The raw HTML for an html-viz card, when the blind view carries it. */
export function htmlSourceFor(gen: Pick<BlindGenerationView, 'extraArtifacts'>): string | null {
  const html = gen.extraArtifacts?.['html'];
  return typeof html === 'string' ? html : null;
}

export function formatCredits(credits: number | null | undefined): string {
  if (credits == null) return '—';
  return `${credits} cr`;
}

export function formatUsd(usd: number | null | undefined): string {
  if (usd == null) return '—';
  return `$${usd.toFixed(usd < 1 ? 3 : 2)}`;
}

export function formatLatency(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
